import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { LoginForm } from '../auth/LoginForm';
import { EmailVerification } from '../auth/EmailVerification';
import { MFAEnrollment } from '../auth/MFAEnrollment';
import { MFAChallenge } from '../auth/MFAChallenge';
import { OnboardingWizard } from '../onboarding/OnboardingWizard';
import { LoadingSpinner } from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading: authLoading, mfaRequired, hasMFA } = useAuth();
  const { organization, loading: orgLoading, needsOnboarding } = useOrganization();

  // Add debugging
  console.log('ProtectedRoute render:', {
    authLoading,
    orgLoading,
    user: user?.email,
    emailVerified: user?.emailVerified,
    mfaRequired,
    hasMFA,
    hasOrganization: !!organization,
    needsOnboarding
  });

  // Show loading while auth or organization is loading
  if (authLoading || (user && orgLoading)) {
    console.log('ProtectedRoute: showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Handle MFA challenge during sign-in - CHECK THIS FIRST!
  if (mfaRequired) {
    console.log('ProtectedRoute: MFA required, showing MFA challenge');
    return <MFAChallenge />;
  }

  if (!user) {
    console.log('ProtectedRoute: no user, showing login form');
    return <LoginForm />;
  }

  if (!user.emailVerified) {
    console.log('ProtectedRoute: email not verified, showing email verification');
    return <EmailVerification />;
  }

  // Enforce mandatory MFA enrollment for all users
  if (!hasMFA) {
    console.log('ProtectedRoute: MFA not enrolled, showing MFA enrollment');
    return <MFAEnrollment />;
  }

  // Check organization status after all auth checks pass
  if (needsOnboarding || !organization) {
    console.log('ProtectedRoute: organization onboarding required, showing onboarding wizard');
    return <OnboardingWizard />;
  }

  console.log('ProtectedRoute: all checks passed, showing app content');
  return <>{children}</>;
};